import { Button, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useUploadIdentityDocuments } from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import { expiringRelatedPartiesKeys } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { partyIdentificationImagesKeys } from "Api/Queries/Party/usePartyIdentificationImagesQuery";
import { ProcessingOcr } from "Components/Onboarding/shared/ProcessingOcr";
import { DragNDrop } from "Components/Shared/DragNDrop";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Account.FirstDocumentUpload;
const FORM_RESOURCES = Resources.Onboarding.Steps.Documents.Forms;
const EXPIRATION_RESOURCES = Resources.Components.DocumentExpirationWarning;
const COMMON_RESOURCES = Resources.Common;

type Props = {
  partyPublicID: string;
  isExpired?: boolean;
};

export const FirstDocumentUpload = ({ partyPublicID, isExpired }: Props) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [frontSide, setFrontSide] = useState<File | null>(null);
  const [backSide, setBackSide] = useState<File | null>(null);
  const queryClient = useQueryClient();
  const { t } = useResource();

  const { mutate: upload, isPending } = useUploadIdentityDocuments({
    successMessage: t(EXPIRATION_RESOURCES.UploadSuccess),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: partyIdentificationImagesKeys.byPublicId(partyPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: expiringRelatedPartiesKeys.all,
      });
      handleCancel();
    },
  });

  const isFormValid = !!frontSide && !!backSide;

  const handleCancel = () => {
    setFrontSide(null);
    setBackSide(null);
    setIsFormVisible(false);
  };

  const handleUpload = () => {
    if (!frontSide || !backSide) {
      return;
    }

    upload({
      partyPublicID,
      documents: {
        firstIDCardFrontSide: frontSide,
        firstIDCardBackSide: backSide,
      },
    });
  };

  if (isPending) {
    return <ProcessingOcr />;
  }

  return (
    <Stack gap={2}>
      {!isFormVisible && (
        <Button
          variant="outlined"
          color={isExpired ? "error" : "primary"}
          onClick={() => {
            setIsFormVisible(true);
          }}
        >
          {t(EXPIRATION_RESOURCES.UploadNewDocument)}
        </Button>
      )}

      {isFormVisible && (
        <>
          <DragNDrop
            file={frontSide}
            onChange={setFrontSide}
            title={t(FORM_RESOURCES.FrontSide)}
            description={t(RESOURCES.FileDescription)}
          />
          <DragNDrop
            file={backSide}
            onChange={setBackSide}
            title={t(FORM_RESOURCES.BackSide)}
            description={t(RESOURCES.FileDescription)}
          />
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!isFormValid}
          >
            {t(RESOURCES.UploadButton)}
          </Button>
          <Button variant="text" onClick={handleCancel}>
            {t(COMMON_RESOURCES.Cancel)}
          </Button>
        </>
      )}
    </Stack>
  );
};
